// frontend/src/pages/PayrollGuide/content.ts
// Static content for the Payroll Guide page.
// Copy lives here, UI lives in index.tsx — separation makes translation + edits safer.

import type { Step, Faq } from "./types";

export const STEPS: ReadonlyArray<Step> = [
  {
    num: 1,
    title: "Add your team",
    time: "5 to 10 min per person",
    intro:
      "Start by adding everyone you pay. For each person, you enter their name, contact info, hire date, pay rate, and tax form details (TD1 for Canada, W-4 for US, P45 for UK, TFN declaration for Australia). Novala adapts the form based on the country your business is in.",
    why:
      "Tax calculations cannot happen without each employee's tax form. Novala uses this info to figure out how much federal, provincial, and CPP/EI to withhold from every paycheque.",
    extra:
      "You can also invite employees to fill in their own details by email. They get a secure link, enter their info, and you save time.",
    cta: "Go to Employees",
    route: "/payroll/employees",
  },
  {
    num: 2,
    title: "Set up your pay schedule",
    time: "2 min",
    intro:
      'Tell Novala how often you pay your team. The options are weekly, bi-weekly, semi-monthly (the 15th and end of the month), or monthly.',
    why:
      "Your pay schedule controls when paydays land and what time period each run covers. Novala uses it to count down to payday on your dashboard and to lock the right pay period for each run.",
    extra:
      "You can have different schedules for different groups of employees, for example bi-weekly for hourly staff and monthly for salaried managers.",
    cta: "Go to Payroll settings",
    route: "/payroll/settings",
  },
  {
    num: 3,
    title: "Connect a bank account",
    time: "5 min plus 1 to 2 days verify",
    intro:
      "Connect the business chequing account that payroll will draw from. Novala asks for your bank name, account holder name, and the account identifiers for your country (transit and institution for Canada, routing for US, sort code for UK, BSB for Australia).",
    why:
      "Without a connected bank account, employees can only be paid by cheque. Direct deposit needs the account verified first; Novala does this with a small test deposit that takes 1 to 2 business days.",
    extra:
      "Your bank info is encrypted and never shared. It is only used to send payments to your employees and to file payroll taxes.",
    cta: "Connect bank",
    route: "/payroll/overview",
  },
  {
    num: 4,
    title: "Run payroll",
    time: "10 to 15 min per run",
    intro:
      "When payday is coming up, open Run payroll. You will see every employee on this pay schedule, with cells for regular hours, stat holiday hours, and any one-time stat pay or bonus. Type the hours, and Novala calculates gross pay, deductions, and net pay live.",
    why:
      "This is the actual paycheque-cutting moment. Novala saves your work as you go (auto-save), so you can leave and come back. When the numbers look right, you preview, then finalize. The run is locked.",
    extra:
      "You can export the run as Excel or PDF for your records, or save it for later if you are not ready to finalize.",
    cta: "Run payroll",
    route: "/payroll/run",
  },
  {
    num: 5,
    title: "Pay your payroll taxes",
    time: "Monthly or quarterly",
    intro:
      "Every paycheque includes deductions for federal tax, provincial or state tax, and social contributions (CPP and EI in Canada, Social Security in the US, NI in the UK, super in Australia). You owe the tax agency this amount, plus the employer portion.",
    why:
      "Missing tax remittance deadlines means penalties and interest. Novala tracks what is owed and when, and shows the next deadline on your Payroll overview so nothing slips through.",
    extra:
      "Once you sign the tax authorization forms, Novala can e-file and pay these for you automatically. Until then, you remit them manually using the totals Novala calculates.",
    cta: "View tax dashboard",
    route: "/payroll/settings",
  },
];

export const FAQS: ReadonlyArray<Faq> = [
  {
    q: "How long does it take to set up payroll on Novala?",
    a: "Most businesses are ready to run their first payroll in 30 to 60 minutes. Adding employees takes the longest because of all the tax info per person. Once setup is done, each pay run takes 10 to 15 minutes.",
  },
  {
    q: "What if I have already paid employees this year on another system?",
    a: "You can import your year-to-date payroll history so that T4s and year-end forms are accurate. Have your pay stubs or payroll reports ready before you start.",
  },
  {
    q: "Can I pay employees in different countries?",
    a: "Yes. Novala supports payroll in the United States, Canada, the United Kingdom, Australia, New Zealand, Singapore, Japan, Germany, France, and South Africa, with country-specific tax forms and bank details. Employees in any other country can also be added by entering their account identifiers manually.",
  },
  {
    q: "What if direct deposit is not set up yet?",
    a: "You can still run payroll. Novala falls back to cheque as the payment method for everyone until direct deposit is connected and verified. You can switch to direct deposit any time after verification finishes.",
  },
  {
    q: "Is my data safe?",
    a: "Yes. All payroll data, bank info, and tax IDs are encrypted at rest and in transit. Novala never shares your information with third parties beyond what is required to pay employees and file taxes.",
  },
  {
    q: "Can I change my pay schedule later?",
    a: "Yes, you can change your pay schedule at any time in Payroll settings. The new schedule takes effect from the next pay period; existing finalized runs are not affected.",
  },
];